import { useState, useCallback, useRef } from "react";

const MIN_SECTION_HEIGHT = 100;

/**
 * Hook for vertical drag-to-resize between a top section and a bottom section.
 *
 * Unlike react-resizable-panels (percentage-based), this preserves the flex layout
 * so the initial position adapts to content height. The user can then drag to override.
 *
 * - `dragHeight = null` → top section uses intrinsic height (flex: "none")
 * - `dragHeight = number` → top section gets a fixed pixel height with overflow: auto
 * - Double-click the separator → resets to null (back to content-adaptive)
 * - Call `resetDragHeight()` when selection changes to re-adapt
 */
export const useVerticalDragResize = () => {
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const topSection = containerRef.current
      ?.firstElementChild as HTMLElement | null;
    if (!topSection || !containerRef.current) return;

    const startY = e.clientY;
    const startHeight = topSection.getBoundingClientRect().height;
    const containerHeight = containerRef.current.clientHeight;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientY - startY;
      const newHeight = Math.max(
        MIN_SECTION_HEIGHT,
        Math.min(startHeight + delta, containerHeight - MIN_SECTION_HEIGHT),
      );
      setDragHeight(newHeight);
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
  }, []);

  const resetDragHeight = useCallback(() => setDragHeight(null), []);

  return { dragHeight, containerRef, handleDragStart, resetDragHeight };
};
